import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AUTH_CALLBACK_T_KEYS } from "@/constants/authCallbackConstants";
import { useAuth0 } from "@auth0/auth0-react";
import { ENV_APP_BASE_URL } from "@/env";
import { useTranslation } from "react-i18next";
import lucidBearLogo from "@/assets/bear_logo.svg.svg";
import lucidWordmarkLogo from "@/assets/lucid_logo.svg.svg";
import "./AuthCallbackResponse.css";

function normalizeStatus(status?: string | null) {
  return (status ?? "").trim().toLowerCase();
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "";
}

function resolveErrorPresentation(
  status: string | null,
  errorCode: string | null,
  errorDescription: string | null,
  authErrorMessage: string,
) {
  const haystack = [status, errorCode, errorDescription, authErrorMessage]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    haystack.includes("email_not_verified") ||
    (haystack.includes("verify") && haystack.includes("email"))
  ) {
    return {
      messageKey: AUTH_CALLBACK_T_KEYS.messages.emailVerification,
      recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.emailVerification,
    };
  }

  if (
    haystack.includes("password") &&
    (haystack.includes("expired") || haystack.includes("rotation") || haystack.includes("reset"))
  ) {
    return {
      messageKey: AUTH_CALLBACK_T_KEYS.messages.passwordRotation,
      recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.passwordRotation,
    };
  }

  if (
    haystack.includes("not entitled") ||
    haystack.includes("entitlement") ||
    haystack.includes("unauthorized_client") ||
    (haystack.includes("unauthorized") && haystack.includes("role"))
  ) {
    return {
      messageKey: AUTH_CALLBACK_T_KEYS.messages.notEntitled,
      recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.unauthorized,
    };
  }

  if (haystack.includes("organization") && (haystack.includes("disabled") || haystack.includes("inactive"))) {
    return {
      messageKey: AUTH_CALLBACK_T_KEYS.messages.orgDisabled,
      recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.unauthorized,
    };
  }

  if (
    haystack.includes("user is blocked") ||
    haystack.includes("blocked_user") ||
    (haystack.includes("blocked") && haystack.includes("user"))
  ) {
    return {
      messageKey: AUTH_CALLBACK_T_KEYS.messages.blockedUser,
      recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.unauthorized,
    };
  }

  if (haystack.includes("invalid_invitation") || (haystack.includes("invitation") && haystack.includes("invalid"))) {
    return {
      messageKey: AUTH_CALLBACK_T_KEYS.messages.invalidInvitation,
      recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.invitationRetry,
    };
  }

  if (haystack.includes("expired_invitation") || (haystack.includes("invitation") && haystack.includes("expired"))) {
    return {
      messageKey: AUTH_CALLBACK_T_KEYS.messages.expiredInvitation,
      recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.invitationRetry,
    };
  }

  if (haystack.includes("invitation_already_used") || (haystack.includes("invitation") && haystack.includes("already_used"))) {
    return {
      messageKey: AUTH_CALLBACK_T_KEYS.messages.invitationAlreadyUsed,
      recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.invitationRetry,
    };
  }

  return {
    messageKey: AUTH_CALLBACK_T_KEYS.messages.generic,
    recoveryKey: AUTH_CALLBACK_T_KEYS.recovery.generic,
  };
}

export default function AuthCallbackResponse() {
  const location = useLocation();
  const { t } = useTranslation();
  const { error, logout } = useAuth0();

  const searchParams = new URLSearchParams(location.search);
  const queryStatus = searchParams.get("status");
  const queryError = searchParams.get("error");
  const queryErrorDescription = searchParams.get("error_description");
  const queryCode = searchParams.get("code");
  const authErrorMessage = getErrorMessage(error);

  const normalizedStatus = normalizeStatus(queryStatus);
  const hasStatusError = ["error", "failed", "failure"].includes(normalizedStatus);
  const hasCallbackError = Boolean(error || queryError || queryErrorDescription || hasStatusError);
  const hasLoggedRef = useRef(false);
  useEffect(() => {
    if (!hasCallbackError || hasLoggedRef.current) {
      return;
    }

    hasLoggedRef.current = true;
  }, [
    authErrorMessage,
    hasCallbackError,
    location.pathname,
    queryError,
    queryErrorDescription,
    queryStatus,
  ]);

  if (!hasCallbackError) {
    return (
      <div
        className="authCallbackLoader"
        role="status"
        aria-live="polite"
        aria-label="Loading"
        data-testid="callback-loader"
      >
        Loading...
      </div>
    );
  }

  const content = resolveErrorPresentation(
    queryStatus,
    queryError,
    queryErrorDescription,
    authErrorMessage,
  );

  const retryLogin = async () => {
    try {
      await logout({ logoutParams: { returnTo: ENV_APP_BASE_URL } });
    } catch {
      window.location.assign("/");
    }
  };

  return (
    <div className="authCallbackPage">
      <main className="authCallbackBody">
        <div className="authCallbackCenterContent">
          <div className="authCallbackBrandBar">
            <img
              className="authCallbackBrandWordmark"
              src={lucidWordmarkLogo}
              alt={t(AUTH_CALLBACK_T_KEYS.logoAlt.wordmark)}
            />
            <img
              className="authCallbackBrandBear"
              src={lucidBearLogo}
              alt={t(AUTH_CALLBACK_T_KEYS.logoAlt.bear)}
            />
          </div>
          <p className="authCallbackPlainText color-text-muted">
            {t(content.messageKey)}
          </p>
          <p className="authCallbackPlainText color-text-muted">
            {t(content.recoveryKey)}
          </p>
          {queryCode && (
            <p className="authCallbackPlainText color-text-muted">
              {t(AUTH_CALLBACK_T_KEYS.referenceCode, { code: queryCode })}
            </p>
          )}
          <div className="authCallbackActions">
            <button
              type="button"
              className="authCallbackActionButton"
              onClick={retryLogin}
            >
              {t(AUTH_CALLBACK_T_KEYS.tryAgain)}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}